import dayjs, {Dayjs} from "dayjs";

export type DateRangePresetId =
    "today"
    | "yesterday"
    | "this-week"
    | "last-week"
    | "last-7-days"
    | "last-30-days"
    | "this-month"
    | "last-month"
    | "this-year"
    | "last-year";

export interface DateRangePreset {
    id: DateRangePresetId;
    label: string;
    value: () => [Dayjs, Dayjs];
}

export const LAST_7_DAYS_INCLUSIVE_OFFSET = 6;
export const LAST_30_DAYS_INCLUSIVE_OFFSET = 29;

export function createDateRangePresets(): DateRangePreset[] {
    return [
        {
            id: "today",
            label: "Today",
            value: () => [dayjs(), dayjs()]
        },
        {
            id: "yesterday",
            label: "Yesterday",
            value: () => {
                const d = dayjs().subtract(1, "day");

                return [d, d];
            }
        },
        {
            id: "this-week",
            label: "This week",
            value: () => {
                const week = dayjs();

                return [week.startOf("week"), week.endOf("week")];
            }
        },
        {
            id: "last-week",
            label: "Last week",
            value: () => {
                const week = dayjs().subtract(1, "week");

                return [week.startOf("week"), week.endOf("week")];
            }
        },
        {
            id: "last-7-days",
            label: "Last 7 days",
            value: () => [dayjs().subtract(LAST_7_DAYS_INCLUSIVE_OFFSET, "day"), dayjs()]
        },
        {
            id: "last-30-days",
            label: "Last 30 days",
            value: () => [dayjs().subtract(LAST_30_DAYS_INCLUSIVE_OFFSET, "day"), dayjs()]
        },
        {
            id: "this-month",
            label: "This month",
            value: () => {
                const month = dayjs();

                return [month.startOf("month"), month.endOf("month")];
            }
        },
        {
            id: "last-month",
            label: "Last month",
            value: () => {
                const month = dayjs().subtract(1, "month");

                return [month.startOf("month"), month.endOf("month")];
            }
        },
        {
            id: "this-year",
            label: "This year",
            value: () => {
                const year = dayjs();

                return [year.startOf("year"), year.endOf("year")];
            }
        },
        {
            id: "last-year",
            label: "Last year",
            value: () => {
                const year = dayjs().subtract(1, "year");

                return [year.startOf("year"), year.endOf("year")];
            }
        },
    ];
}

export function resolveDateRangePresetValue(id: unknown): [Dayjs, Dayjs] | undefined {
    if (typeof id !== "string") {
        return undefined;
    }

    return createDateRangePresets().find(preset => preset.id === id)?.value();
}

export function findDateRangePresetId(from: Dayjs, to: Dayjs): DateRangePresetId | undefined {
    return createDateRangePresets().find(preset => {
        const [presetFrom, presetTo] = preset.value();

        return sameDate(from, presetFrom) && sameDate(to, presetTo);
    })?.id;
}

export function parseFilterDate(value: unknown, fallback: Dayjs) {
    if (dayjs.isDayjs(value)) {
        return value.isValid() ? value : fallback;
    }

    if (typeof value === "string") {
        const parsed = dayjs(value);

        return parsed.isValid() ? parsed : fallback;
    }

    return fallback;
}

function sameDate(a: Dayjs, b: Dayjs) {
    return a.format("YYYY-MM-DD") === b.format("YYYY-MM-DD");
}
